import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from './store/authStore';
import { TemplateStudio } from './pages/TemplateStudio';
import { TemplateList } from './pages/TemplateList';
import { ClauseLibrary } from './pages/ClauseLibrary';
import { Dashboard } from './pages/Dashboard';
import { ClientList } from './pages/ClientList';
import { MatterList } from './pages/MatterList';
import { DocumentList } from './pages/DocumentList';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { HelpDocs } from './pages/HelpDocs';
import Login from './pages/Login';

// Lazy-loaded Lawity Pages for code splitting
const RainmakerPage = lazy(() => import('./pages/RainmakerPage').then(m => ({ default: m.RainmakerPage })));
const ConnectorsPage = lazy(() => import('./pages/ConnectorsPage').then(m => ({ default: m.ConnectorsPage })));
const AIAgentsPage = lazy(() => import('./pages/AIAgentsPage').then(m => ({ default: m.AIAgentsPage })));
const DealEnginePage = lazy(() => import('./pages/DealEnginePage').then(m => ({ default: m.DealEnginePage })));
const BriefingPage = lazy(() => import('./pages/BriefingPage').then(m => ({ default: m.BriefingPage })));
const DecisionQueuePage = lazy(() => import('./pages/DecisionQueuePage').then(m => ({ default: m.DecisionQueuePage })));

// Growth Engine Pages
const ReciprocityDashboard = lazy(() => import('./pages/ReciprocityDashboard').then(m => ({ default: m.ReciprocityDashboard })));
const InstaBragGenerator = lazy(() => import('./pages/InstaBragGenerator').then(m => ({ default: m.InstaBragGenerator })));
const WatchdogDashboard = lazy(() => import('./pages/WatchdogDashboard').then(m => ({ default: m.WatchdogDashboard })));
const NetworkBrokerDashboard = lazy(() => import('./pages/NetworkBrokerDashboard').then(m => ({ default: m.NetworkBrokerDashboard })));

// Billing & Practice Pages
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
import { NotificationProvider, NotificationBell, ToastContainer } from './components/Notifications';
import { CommandPalette, useCommandPalette } from './components/CommandPalette';
import { AIAssistant, useAIAssistant } from './components/AIAssistant';
import { AITemplateGenerator } from './components/AITemplateGenerator';
import { AIDocumentAnalyzer } from './components/AIDocumentAnalyzer';
import { AISmartFill } from './components/AISmartFill';
import { AIClauseSuggestions } from './components/AIClauseSuggestions';
import { AIFloatingButton } from './components/AIFloatingButton';
import { KeyboardShortcuts } from './components/KeyboardShortcuts';
import { OnboardingTour, useOnboarding } from './components/OnboardingTour';
import {
  Loader2, LayoutDashboard, FileText, Users, Briefcase,
  FolderOpen, Settings as SettingsIcon, LogOut, Scale, ChevronRight,
  Files, HelpCircle, BarChart2, Keyboard, Target, Plug, Brain,
  Headphones, CheckSquare, Sparkles, Shield, Network, TrendingUp,
  DollarSign
} from 'lucide-react';

// Handshake icon doesn't exist in this version, using Briefcase as fallback
const Handshake = Briefcase;

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-accent-500 animate-spin mx-auto mb-3" />
        <p className="text-primary-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-accent-500 animate-spin mx-auto mb-4" />
          <p className="text-primary-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Public Route (redirects to app if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuth();

  if (!initialized) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-accent-500 animate-spin mx-auto mb-4" />
          <p className="text-primary-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Dashboard Layout with sidebar navigation and AI features
function DashboardLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const commandPalette = useCommandPalette();
  const aiAssistant = useAIAssistant();
  const { showOnboarding, setShowOnboarding } = useOnboarding();
  
  // Modal States
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  const [showDocumentAnalyzer, setShowDocumentAnalyzer] = useState(false);
  const [showSmartFill, setShowSmartFill] = useState(false);
  const [showClauseSuggestions, setShowClauseSuggestions] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette is handled by useCommandPalette hook
      
      // Keyboard shortcuts modal: Cmd+?
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShowKeyboardShortcuts(prev => !prev);
      }
      
      // AI Assistant: Cmd+Shift+A (but not conflicting with analyze)
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        aiAssistant.open({ currentPage: location.pathname });
      }
      
      // Quick navigation
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        switch (e.key) {
          case '1':
            e.preventDefault();
            navigate('/dashboard');
            break;
          case '2':
            e.preventDefault();
            navigate('/templates');
            break;
          case '3':
            e.preventDefault();
            navigate('/clauses');
            break;
          case '4':
            e.preventDefault();
            navigate('/clients');
            break;
          case '5':
            e.preventDefault();
            navigate('/matters');
            break;
          case '6':
            e.preventDefault();
            navigate('/documents');
            break;
          // Lawity AI & Automation
          case '7':
            e.preventDefault();
            navigate('/ai-agents');
            break;
          case '8':
            e.preventDefault();
            navigate('/rainmaker');
            break;
          case '9':
            e.preventDefault();
            navigate('/deal-engine');
            break;
          // Calm Law
          case '0':
            e.preventDefault();
            navigate('/decisions');
            break;
        }
      }
      
      // AI Feature shortcuts (Cmd+Shift)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
        switch (e.key.toLowerCase()) {
          case 'g':
            e.preventDefault();
            setShowTemplateGenerator(true);
            break;
          case 'a':
            e.preventDefault();
            setShowDocumentAnalyzer(true);
            break;
          case 'f':
            e.preventDefault();
            setShowSmartFill(true);
            break;
          case 'c':
            e.preventDefault();
            setShowClauseSuggestions(true);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location.pathname, aiAssistant]);

  const navSections = [
    {
      title: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/reports', label: 'Reports', icon: BarChart2 },
      ],
    },
    {
      title: 'Documents',
      items: [
        { path: '/templates', label: 'Templates', icon: FileText },
        { path: '/clauses', label: 'Clause Library', icon: FolderOpen },
        { path: '/documents', label: 'Documents', icon: Files },
      ],
    },
    {
      title: 'Practice',
      items: [
        { path: '/clients', label: 'Clients', icon: Users },
        { path: '/matters', label: 'Matters', icon: Briefcase },
        { path: '/billing', label: 'Billing', icon: DollarSign },
      ],
    },
    {
      title: 'AI & Automation',
      items: [
        { path: '/ai-agents', label: 'AI Agents', icon: Brain },
        { path: '/rainmaker', label: 'Rainmaker', icon: Target },
        { path: '/connectors', label: 'Connectors', icon: Plug },
        { path: '/deal-engine', label: 'Deal Engine', icon: Handshake },
      ],
    },
    {
      title: 'Calm Law',
      items: [
        { path: '/briefing', label: 'Briefing', icon: Headphones },
        { path: '/decisions', label: 'Decisions', icon: CheckSquare },
      ],
    },
    {
      title: 'Growth Engine',
      items: [
        { path: '/reciprocity', label: 'Reciprocity', icon: TrendingUp },
        { path: '/insta-brag', label: 'Insta-Brag', icon: Sparkles },
        { path: '/watchdog', label: 'Watchdog', icon: Shield },
        { path: '/network-broker', label: 'Network Broker', icon: Network },
      ],
    },
  ];

  // Handle generated template from AI
  const handleTemplateGenerated = (content: string, variables: any[]) => {
    console.log('Template generated:', { content, variables });
    setShowTemplateGenerator(false);
    navigate('/templates/new');
  };

  return (
    <NotificationProvider>
      <div className="h-screen flex bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
        {/* Sidebar */}
        <aside className="w-64 bg-primary-900/50 border-r border-primary-700 flex flex-col">
          {/* Logo */}
          <div className="p-4 border-b border-primary-700">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-display font-bold text-white">Lawity</h1>
                <p className="text-xs text-primary-400">Legal Practice Management</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-6 overflow-auto">
            {navSections.map((section) => (
              <div key={section.title}>
                <p className="px-3 mb-2 text-xs font-semibold text-primary-500 uppercase tracking-wider">
                  {section.title}
                </p>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.path || 
                      (item.path !== '/dashboard' && item.path !== '/reports' && location.pathname.startsWith(item.path));
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-accent-500/20 text-accent-400'
                            : 'text-primary-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-primary-700 space-y-2">
            <Link
              to="/settings"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                location.pathname === '/settings'
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-primary-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <SettingsIcon className="w-5 h-5" />
              Settings
            </Link>
            <Link
              to="/help"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                location.pathname === '/help'
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-primary-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <HelpCircle className="w-5 h-5" />
              Help & Docs
            </Link>
            <button
              onClick={() => setShowKeyboardShortcuts(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-primary-300 hover:bg-white/5 hover:text-white"
            >
              <Keyboard className="w-5 h-5" />
              Shortcuts
              <kbd className="ml-auto text-xs bg-primary-700 px-1.5 py-0.5 rounded">⌘/</kbd>
            </button>
          </div>

          {/* User section */}
          <div className="p-4 border-t border-primary-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
                <span className="text-accent-400 font-medium">
                  {user?.email?.[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                <p className="text-xs text-primary-400">Administrator</p>
              </div>
              <button 
                onClick={() => signOut()}
                className="p-2 text-primary-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <header className="h-14 bg-primary-900/30 border-b border-primary-700 flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              {/* Breadcrumb could go here */}
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>

        {/* Toast notifications */}
        <ToastContainer />

        {/* AI Floating Button */}
        <AIFloatingButton
          onOpenChat={() => aiAssistant.open({ currentPage: location.pathname })}
          onOpenGenerator={() => setShowTemplateGenerator(true)}
          onOpenAnalyzer={() => setShowDocumentAnalyzer(true)}
          onOpenSmartFill={() => setShowSmartFill(true)}
          onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
          showAnalyzer={location.pathname.includes('/documents')}
          showSmartFill={location.pathname.includes('/templates')}
          showClauseSuggestions={location.pathname.includes('/templates') || location.pathname.includes('/clauses')}
        />

        {/* Command palette with AI features */}
        <CommandPalette 
          isOpen={commandPalette.isOpen} 
          onClose={commandPalette.close}
          onOpenAIAssistant={() => aiAssistant.open({ currentPage: location.pathname })}
          onOpenTemplateGenerator={() => setShowTemplateGenerator(true)}
          onOpenDocumentAnalyzer={() => setShowDocumentAnalyzer(true)}
          onOpenSmartFill={() => setShowSmartFill(true)}
          onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
        />

        {/* AI Assistant Panel */}
        <AIAssistant 
          isOpen={aiAssistant.isOpen} 
          onClose={aiAssistant.close}
          context={aiAssistant.context}
        />

        {/* AI Template Generator Modal */}
        <AITemplateGenerator
          isOpen={showTemplateGenerator}
          onClose={() => setShowTemplateGenerator(false)}
          onGenerated={handleTemplateGenerated}
        />

        {/* AI Document Analyzer Modal */}
        <AIDocumentAnalyzer
          isOpen={showDocumentAnalyzer}
          onClose={() => setShowDocumentAnalyzer(false)}
          documentContent="Sample document content for analysis..."
          documentType="Legal Document"
        />

        {/* AI Smart Fill Modal */}
        <AISmartFill
          isOpen={showSmartFill}
          onClose={() => setShowSmartFill(false)}
          templateVariables={[
            { name: 'grantor.name', type: 'text', required: true, label: 'Grantor Name' },
            { name: 'grantee.name', type: 'text', required: true, label: 'Grantee Name' },
            { name: 'property.address', type: 'text', required: true, label: 'Property Address' },
            { name: 'consideration', type: 'number', required: false, label: 'Consideration' },
          ]}
          onFill={(variables) => {
            console.log('Variables filled:', variables);
          }}
        />

        {/* AI Clause Suggestions Modal */}
        <AIClauseSuggestions
          isOpen={showClauseSuggestions}
          onClose={() => setShowClauseSuggestions(false)}
          documentType="Warranty Deed"
          jurisdiction="Wisconsin"
          existingClauses={['Standard Warranty', 'Legal Description']}
          onInsertClause={(content, name) => {
            console.log('Insert clause:', { name, content });
          }}
        />

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcuts
          isOpen={showKeyboardShortcuts}
          onClose={() => setShowKeyboardShortcuts(false)}
        />

        {/* Onboarding Tour */}
        <OnboardingTour
          isOpen={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          onComplete={() => setShowOnboarding(false)}
        />
      </div>
    </NotificationProvider>
  );
}

export default function App() {
  return (
    <div className="h-full">
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        
        {/* Protected routes with dashboard layout */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Dashboard />
              </DashboardLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/reports" 
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Reports />
              </DashboardLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/templates" 
          element={
            <ProtectedRoute>
              <TemplateList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/templates/new" 
          element={
            <ProtectedRoute>
              <TemplateStudio />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/templates/:id" 
          element={
            <ProtectedRoute>
              <TemplateStudio />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clauses" 
          element={
            <ProtectedRoute>
              <ClauseLibrary />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clients" 
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ClientList />
              </DashboardLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/matters" 
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <MatterList />
              </DashboardLayout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DocumentList />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <BillingPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings" 
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Settings />
              </DashboardLayout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <HelpDocs />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* AI & Automation Routes (lazy-loaded) */}
        <Route
          path="/ai-agents"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <AIAgentsPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rainmaker"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <RainmakerPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/connectors"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <ConnectorsPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal-engine"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <DealEnginePage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Calm Law Routes (lazy-loaded) */}
        <Route
          path="/briefing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <BriefingPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/decisions"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <DecisionQueuePage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Growth Engine Routes (lazy-loaded) */}
        <Route
          path="/reciprocity"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <ReciprocityDashboard />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/insta-brag"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <InstaBragGenerator />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/watchdog"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <WatchdogDashboard />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/network-broker"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<PageLoader />}>
                  <NetworkBrokerDashboard />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  );
}
